import React, { useState, useEffect } from 'react';
import { Image, Divider, Form, Input, Button, Row, Col, Upload } from 'antd';
import * as Icon from 'react-feather';
import './profile.css';
import { useQuery } from 'react-query';
import { api_srv } from 'services';
import Loader from 'components/widgets/loader';
import styled from 'styled-components';
import { base_url, file_url } from 'services/apiSrv';
import { show_toast } from 'utils/helpers';

import PdfIcon from 'assets/images/pdf.png';
import DocIcon from 'assets/images/doc-icon.png';
import FileIcon from 'assets/images/file-icon.png';
import ExcelIcon from 'assets/images/Excel-Icon.png';
import { fileExtension } from 'utils/helpers';

const Document = styled.div`
  margin-bottom: 40px;
  display: grid;
  grid-gap: 50px;
`;

const DocumentRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
`;

const DocumentTitle = styled.a`
  line-height: 1.5em;
  font-size: 15px;
  margin-top: 1rem;
`;

const DocumentLogo = styled.img`
  max-width: 70px;
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`;

const DocumentLogoContainer = styled.div`
  display: none;
  &:hover {
    display: block;
    background-color: black;
    width: 100px;
    height: 100px;
    margin: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.3s ease-in-out;
  }
`;

const DeleteDocument = styled.div`
  ${DocumentLogoContainer}
  &:hover {
    display: block;
  }
`;

const checkExtension = (file_path) => {
  let ext = fileExtension(file_path || '');
  switch (ext) {
    case 'pdf':
      return PdfIcon;
    case 'xls':
    case 'xlsx':
      return ExcelIcon;
    case 'doc':
    case 'docx':
      return DocIcon;
    default:
      return FileIcon;
  }
};

// File upload function (reused from KycSetup)
const uploadProductImage = async (options) => {
  const { onSuccess, onError, file } = options;

  try {
    let res = await (await api_srv).upload_api('productimage', file);
    console.log('Product Image===:', res);
    onSuccess(res.data[0].fileUrl);
    show_toast(`${file.name} uploaded successfully.`, 'success');
    return res.data[0].fileUrl;
  } catch (err) {
    console.error('File upload error:', err);
    onError(err);
    show_toast(`${file.name} upload failed.`, 'error');
  }
};

const Profile = () => {
  const [form] = Form.useForm();
  const [kyc, setKyc] = useState({
    documents: [
      { name: 'krapin', path: '' },
      { name: 'certofinccoperation', path: '' },
      { name: 'memoofassociation', path: '' },
    ],
    fullName: '',
    contact: '',
    idNumber: '',
    kraPin: '',
    category: '',
    iraCode: '',
    address: '',
    description: '',
  });

  async function fetch_intermediary_details() {
    let resp = await (await api_srv).fetchIntermediaryProfile();
    return resp.data;
  }

  const { data, isLoading, isSuccess } = useQuery('profile details', fetch_intermediary_details);
  console.log("KYC", data?.[0].kyc)

  const kycDocuments = data?.[0].kyc.documents ?? [];

  // Initialize form and kyc state with API data
  useEffect(() => {
    if (isSuccess && data?.[0]) {
      const initialData = {
        fullName: data[0].intermediaryNames || '',
        contact: data[0].contacts?.[0] || '',
        idNumber: data[0].identification_number || '',
        kraPin: data[0].kraPin || '',
        category: data[0].category || '',
        iraCode: data[0].licenseNumber || '',
        address: data[0].intermediaryLocation?.coordinates?.join(', ') || '',
        description: data[0].description || '',
        'KRA Certificate': data?.[0].kyc?.krapin || '',
        'Incorporation Certificate': data?.[0].kyc?.incorporation_certificate_file_path || '',
        'Memo of Association': data?.[0].kyc?.memo_of_association_file_path || '',
      };

      setKyc({
        documents: [
          { name: 'krapin', path: data?.[0].kyc?.kra_certificate_file_path || '' },
          { name: 'certofinccoperation', path: data?.[0].kyc?.incorporation_certificate_file_path || '' },
          { name: 'memoofassociation', path: data?.[0].kyc?.memo_of_association_file_path || '' },
        ],
        ...initialData,
      });

      form.setFieldsValue(initialData);
    }
  }, [data, isSuccess, form]);

  // Sync kyc state with form changes
  const onValuesChange = (changedValues, allValues) => {
    setKyc((prev) => ({
      ...prev,
      ...allValues,
      documents: prev.documents.map((doc) => {
        if (doc.name === 'krapin' && allValues['KRA Certificate']) {
          return { ...doc, path: allValues['KRA Certificate'] };
        }
        if (doc.name === 'certofinccoperation' && allValues['Incorporation Certificate']) {
          return { ...doc, path: allValues['Incorporation Certificate'] };
        }
        if (doc.name === 'memoofassociation' && allValues['Memo of Association']) {
          return { ...doc, path: allValues['Memo of Association'] };
        }
        return doc;
      }),
    }));
  };

  // Custom upload handler for documents
  const handleUpload = (documentName, fieldName) => {
    return {
      customRequest: async ({ file, onSuccess, onError }) => {
        try {
          const fileUrl = await uploadProductImage({ file, onSuccess, onError });
          if (fileUrl) {
            setKyc((prev) => ({
              ...prev,
              documents: prev.documents.map((doc) =>
                doc.name === documentName ? { ...doc, path: fileUrl } : doc
              ),
            }));
            form.setFieldsValue({ [fieldName]: fileUrl });
          }
        } catch (err) {
          console.error('Upload failed:', err);
        }
      },
    };
  };

  const onFinish = async (values) => {
    try {
      // Prepare data for API submission
      const updatedData = {
        intermediaryNames: values.fullName,
        contacts: [values.contact],
        identification_number: values.idNumber,
        kraPin: values.kraPin,
        category: values.category,
        licenseNumber: values.iraCode,
        intermediaryLocation: { coordinates: values.address.split(', ') },
        description: values.description,
        kyc: {
          kra_certificate_file_path: values['KRA Certificate'],
          incorporation_certificate_file_path: values['Incorporation Certificate'],
          memo_of_association_file_path: values['Memo of Association'],
        },
      };

      // Optionally: Add API call to update profile
      // await (await api_srv).updateIntermediaryProfile(updatedData);
      show_toast('Profile updated successfully.', 'success');
    } catch (error) {
      console.error('Profile update failed:', error);
      show_toast('Profile update failed.', 'error');
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    show_toast('Please correct the errors in the form.', 'error');
  };

  // Prepare documents for display
  const documents = kycDocuments
    .filter((doc) => doc.path) // Only include documents with valid paths
    .map((doc) => ({
      name:
        doc.name === 'krapin'
          ? 'KRA Certificate'
          : doc.name === 'certofinccoperation'
          ? 'Incorporation Certificate'
          : 'Memo of Association',
      path: doc.path,
    }));

    console.log("documents====", documents)

  return (
    <>
      {isLoading && <Loader />}
      {isSuccess && data?.[0] && (
        <div>
          <div className="hero__image">
            <button className="mt-4 float-right mr-3 btn btn-sm btn-outline-success text-white">
              <Icon.Camera size={14} className="mr-2" /> Change cover
            </button>
            <div className="profile__content">
              <div className="row overlap">
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-body">
                      <div className="profile__image">
                        <Image src={data[0]?.profileImage} fallback={FileIcon} />
                        <h6 className="mt-3">{data[0]?.intermediaryNames}</h6>
                      </div>
                      <Divider />
                      <p className="my-0 text-dark">
                        <Icon.Phone size={16} />
                        <span className="ml-2">{data[0].contacts?.[0] || 'N/A'}</span>
                      </p>
                      <Divider className="my-3" />
                      <p className="my-0 text-dark">
                        <Icon.Mail size={16} />
                        <span className="ml-2">{data[0].primaryEmail || 'N/A'}</span>
                      </p>
                      <Divider className="my-3" />
                      <p className="my-0 text-dark">
                        <Icon.MapPin size={16} />
                        <span className="ml-2">
                          {data[0].intermediaryLocation?.coordinates?.join(', ') || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="card">
                    <div className="card-header">
                      <h6>KYC Details</h6>
                    </div>
                    <div className="card-body">
                      <Form
                        form={form}
                        layout="vertical"
                        onValuesChange={onValuesChange}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              label="Full Name"
                              name="fullName"
                              rules={[{ required: true, message: 'Please enter full name' }]}
                            >
                              <Input placeholder="Full Name" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Contact"
                              name="contact"
                              rules={[{ required: true, message: 'Please enter contact' }]}
                            >
                              <Input placeholder="Contact" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="ID Number" name="idNumber">
                              <Input placeholder="ID Number" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="KRA Pin Number"
                              name="kraPin"
                              rules={[{ required: true, message: 'Please enter KRA Pin' }]}
                            >
                              <Input placeholder="KRA Pin Number" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Category"
                              name="category"
                              rules={[{ required: true, message: 'Please enter category' }]}
                            >
                              <Input placeholder="Category" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="IRA Code"
                              name="iraCode"
                              rules={[{ required: true, message: 'Please enter IRA Code' }]}
                            >
                              <Input placeholder="IRA Code" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Address"
                              name="address"
                              rules={[{ required: true, message: 'Please enter address' }]}
                            >
                              <Input placeholder="Address" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Description" name="description">
                              <Input.TextArea placeholder="Description" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="KRA Certificate"
                              name="KRA Certificate"
                              rules={[{ required: true, message: 'KRA Certificate is required' }]}
                            >
                              <Upload
                                {...handleUpload('krapin', 'KRA Certificate')}
                                accept=".pdf,.jpg,.png"
                                maxCount={1}
                              >
                                <Button>Upload KRA Certificate</Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Incorporation Certificate"
                              name="Incorporation Certificate"
                              rules={[
                                { required: true, message: 'Incorporation Certificate is required' },
                              ]}
                            >
                              <Upload
                                {...handleUpload('certofinccoperation', 'Incorporation Certificate')}
                                accept=".pdf,.jpg,.png"
                                maxCount={1}
                              >
                                <Button>Upload Incorporation Certificate</Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label="Memo of Association"
                              name="Memo of Association"
                              rules={[{ required: true, message: 'Memo of Association is required' }]}
                            >
                              <Upload
                                {...handleUpload('memoofassociation', 'Memo of Association')}
                                accept=".pdf,.jpg,.png"
                                maxCount={1}
                              >
                                <Button>Upload Memo of Association</Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                        </Row>

                        {/* <img src={data?.[0].kyc.documents?.[0]?.path}></img> */}
                        <div className="d-flex">
                          {documents.length > 0 ? (
                            documents.map((doc, index) => (
                              <Document key={index}>
                                <DocumentRow>
                                  <DocumentItem>
                                    {doc.name}
                                    <>
                                      <DocumentLogo src={checkExtension(doc.path)} alt="document" />
                                      <DocumentLogoContainer>
                                        <DeleteDocument>Delete</DeleteDocument>
                                      </DocumentLogoContainer>
                                    </>
                                    <DocumentTitle href={`${doc.path}`} target="_blank">
                                      Preview
                                    </DocumentTitle>
                                  </DocumentItem>
                                </DocumentRow>
                              </Document>
                            ))
                          ) : (
                            <p>No KYC documents available.</p>
                          )}
                        </div>
                        <Form.Item>
                          <Button type="primary" htmlType="submit">
                            Update
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;